import { Button, DialogHeader, DialogTitle, FileDropzone, Heading } from "@sentricmusic/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { RecordingData } from "@/api/queries";

import {
  useAttachAmendmentOnlyMp3ByPreSignedUrlPost,
  useAttachMp3ByPresignedUrlPost,
} from "@/api/mutations";
import {
  useGetGenerateRecordingAttachmentPresignedUrl,
  useGetMyRecording,
  useGetMyRecordingAmendment,
  useGetMyRecordingsDetailed,
} from "@/api/queries";
import FormError from "@/components/FormError";
import LoadingSmall from "@/components/LoadingSmall";
import { useUIStateStore } from "@/store/uiStateStore";

const RECORDING_TYPE = {
  RECORDING: 0,
  AMENDMENT: 1,
} as const;

function isValidGuid(guid: string | null | undefined): boolean {
  return Boolean(guid) && guid !== "00000000-0000-0000-0000-000000000000";
}

type RecordingAttachmentFormProps = {
  artistaccountcode?: string | null;
  recordingamendmentidentifier?: string | null;
  recordingidentifier?: string | null;
  workcode: string;
  closeModal: () => void;
};

function RecordingAttachmentForm({
  artistaccountcode = null,
  recordingamendmentidentifier = null,
  recordingidentifier = null,
  workcode,
  closeModal,
}: RecordingAttachmentFormProps) {
  const { t } = useTranslation("songs");
  const isTunecore = useUIStateStore(state => state.isTunecore);

  const recordingAmendment = useGetMyRecordingAmendment({
    id: recordingamendmentidentifier,
    aac: artistaccountcode,
    wc: workcode,
  });

  const recording = useGetMyRecording({
    id: recordingidentifier,
    aac: artistaccountcode,
    wc: workcode,
  });

  let isSuccess = false;
  let data: RecordingData | undefined;
  let identifier: string | null = null;
  let recordingtype: number = RECORDING_TYPE.RECORDING;

  if (isValidGuid(recordingamendmentidentifier)) {
    identifier = recordingamendmentidentifier;
    isSuccess = recordingAmendment.isSuccess;
    data = recordingAmendment.data;
    recordingtype = RECORDING_TYPE.AMENDMENT;
  }
  else {
    identifier = recordingidentifier;
    isSuccess = recording.isSuccess;
    data = recording.data;
    recordingtype = RECORDING_TYPE.RECORDING;
  }

  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");

  const [mp3, setMp3] = useState<File | null>(null);
  const filename = mp3?.name ?? null;

  const attachAmendmentOnlyPreSignedMutation = useAttachAmendmentOnlyMp3ByPreSignedUrlPost();
  const attachPreSignedMutation = useAttachMp3ByPresignedUrlPost();
  const recordings = useGetMyRecordingsDetailed({
    aac: artistaccountcode,
    wc: workcode,
  });

  const preSignedUrl = useGetGenerateRecordingAttachmentPresignedUrl({
    id: identifier,
    aac: artistaccountcode,
    wc: workcode,
    fn: filename,
    rt: String(recordingtype),
    ct: "audio/mpeg",
  });

  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (artistaccountcode != null && artistaccountcode !== "") {
      void recordingAmendment.refetch();
      void recording.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistaccountcode]);

  useEffect(() => {
    if (recordingamendmentidentifier != null && recordingamendmentidentifier !== "") {
      if (recordingAmendment.isError || recordingAmendment.data === null) {
        closeModal();
      }
    }
    else if (recordingidentifier != null && recordingidentifier !== "") {
      if (recording.isError || recording.data === null) {
        closeModal();
      }
    }
  }, [recordingAmendment, recording, recordingamendmentidentifier, recordingidentifier, closeModal]);

  useEffect(() => {
    void preSignedUrl.refetch();
  }, [filename, preSignedUrl]);

  let putUrl: string | null = null;

  function uploadComplete(): void {
    if (mp3 === null)
      return;

    if (isValidGuid(recordingamendmentidentifier) && recordingAmendment.data) {
      attachAmendmentOnlyPreSignedMutation.reset();
      const payload = {
        mimetype: mp3.type,
        originalfilename: filename,
        artistaccountcode,
        workcode,
        recordingamendmentidentifier: recordingAmendment.data.recordingamendmentidentifier,
        presignedurl: putUrl,
        etag: recordingAmendment.data.etag,
      };

      attachAmendmentOnlyPreSignedMutation.mutate(payload, {
        onSuccess: (response) => {
          if (response.failedvalidation === true) {
            setValidationErrorMessage(response.messages?.join(", ") ?? "");
            setPending(false);
          }
          else {
            void recordings.refetch();
            toast.success(`Successfully attached mp3: ${data?.title}`);
            setPending(false);
            closeModal();
          }
        },
        onError: (error: Error) => {
          setMutationErrorMessage(error.message);
        },
      });
    }
    else if (recording.data) {
      attachPreSignedMutation.reset();
      const payload = {
        mimetype: mp3.type,
        originalfilename: filename,
        artistaccountcode,
        workcode,
        recordingidentifier: recording.data.recordingidentifier,
        presignedurl: putUrl,
        etag: recording.data.etag,
      };

      attachPreSignedMutation.mutate(payload, {
        onSuccess: (response) => {
          if (response.failedvalidation === true) {
            setValidationErrorMessage(response.messages?.join(", ") ?? "");
            setPending(false);
          }
          else {
            void recordings.refetch();
            toast.success(`Successfully attached mp3: ${data?.title}`);
            closeModal();
          }
        },
        onError: (error: Error) => {
          setMutationErrorMessage(error.message);
        },
      });
    }
  }

  function uploadFailed(): void {
    setValidationErrorMessage("There was an error attempting to upload the file.");
    setPending(false);
  }

  function uploadCanceled(): void {
    setValidationErrorMessage(
      "The upload has been canceled by the user or the browser dropped the connection.",
    );
    setPending(false);
  }

  const uploadMP3 = async (): Promise<void> => {
    if (mp3 === null)
      return;

    setValidationErrorMessage("");
    try {
      setPending(true);
      let url = putUrl;
      if (url == null) {
        const response = await preSignedUrl.refetch();
        if (response.data?.url != null && response.data.url !== "") {
          url = response.data.url;
          putUrl = url;
        }
        else {
          console.error("putUrl is undefined");
          setPending(false);
          return;
        }
      }

      const xmlHttpRequest = new XMLHttpRequest();
      xmlHttpRequest.addEventListener("load", uploadComplete, false);
      xmlHttpRequest.addEventListener("error", uploadFailed, false);
      xmlHttpRequest.addEventListener("abort", uploadCanceled, false);
      xmlHttpRequest.open("PUT", url, true);
      xmlHttpRequest.setRequestHeader("Content-Type", mp3.type);
      xmlHttpRequest.send(mp3);
    }
    catch (error) {
      console.error("Error during file upload:", error);
      setValidationErrorMessage("Error during file upload.");
      setPending(false);
    }
  };

  if (isSuccess && data) {
    return (
      <div className="flex flex-col gap-4">
        <DialogHeader className="text-left">
          <DialogTitle asChild>
            <Heading as="h3">{t("songs:attach-mp3-header")}</Heading>
          </DialogTitle>
        </DialogHeader>
        <Heading as="h4">{data.title}</Heading>
        <FileDropzone
          label={String(t("songs:add-mp3-subheader"))}
          accept={{ "audio/mpeg": [".mp3"] }}
          file={mp3}
          onFileAccepted={setMp3}
          onClick={() => setMp3(null)}
          disabled={pending}
        />
        <FormError mutationError={mutationErrorMessage} validationError={validationErrorMessage} />
        <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
          <Button
            id="btnRecordingFormAddRecording"
            variant={isTunecore ? "secondary" : "primary"}
            disabled={pending || mp3 === null}
            onClick={() => { void uploadMP3(); }}
            label={pending ? String(t("common:loading", "Loading...")) : String(t("songs:attach-mp3"))}
          />
        </div>
      </div>
    );
  }
  return <LoadingSmall />;
}
export default RecordingAttachmentForm;
